import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import {
  IconButton,
  Container,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  AccordionActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import { TextField, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import ResponsiveDialogManager from "../dialog/ResponsiveDialogManager";
import ConfirmationDialog from "../dialog/ConfirmationDialog";
import { useSnackbar } from "notistack";
import CreateIcon from "@mui/icons-material/Create";
import {
  Fr,
  Gb,
  Es,
  It,
  Pt,
  Cn,
  De,
  Ru,
  Ro,
  Tr,
  Jp,
  Us,
  Gr,
  Fi,
  Hr,
  Dk,
  Rs,
} from "react-flags-select";
import ReactFlagsSelect from "react-flags-select";
import { useViewport } from "../../helpers";
import { myExpariencesService } from "../../services";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import BarChart from "../charts/BarChart";
import { authorizedCountries } from "../../helpers/country-translated";
import "./survey.css";
import { styled } from "@mui/material/styles";
import ImageCropper from "../imageCropper/ImageCropper";

const PREFIX = "Survey";

const classes = {
  root: `${PREFIX}-root`,
  iconColumn: `${PREFIX}-iconColumn`,
  displayNone: `${PREFIX}-displayNone`,
  linkColumn: `${PREFIX}-linkColumn`,
  helper: `${PREFIX}-helper`,
  details: `${PREFIX}-details`,
  selectFlag: `${PREFIX}-selectFlag`,
  paintingContainer: `${PREFIX}-paintingContainer`,
  formHeader: `${PREFIX}-formHeader`,
  editImageIcon: `${PREFIX}-editImageIcon`,
  flagStyle: `${PREFIX}-flagStyle`,
  centerText: `${PREFIX}-centerText`,
  selectSizeContainer: `${PREFIX}-selectSizeContainer`,
  accordionContainer: `${PREFIX}-accordionContainer`,
  saveSurveyButton: `${PREFIX}-saveSurveyButton`,
  selectImage: `${PREFIX}-selectImage`,
  menuItemContainer: `${PREFIX}-menuItemContainer`,
  selectText: `${PREFIX}-selectText`,
  openMenuItem: `${PREFIX}-openMenuItem`,
  questionLogo: `${PREFIX}-questionLogo`,
  centerButton: `${PREFIX}-centerButton`,
  chartLoaderContainer: `${PREFIX}-chartLoaderContainer`,
  totalAnswers: `${PREFIX}-totalAnswers`,
  accordionRoot: `${PREFIX}-accordionRoot`,
  buttonSpaces: `${PREFIX}-buttonSpaces`,
};

const SurveyContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      marginBottom: "4rem",
      maxWidth: "650px",
      margin: "0 auto",
    },

    [`& .${classes.iconColumn}`]: {
      flexBasis: "50%",
      position: "relative",
    },

    [`& .${classes.displayNone}`]: {
      display: "none",
    },

    [`& .${classes.linkColumn}`]: {
      flexBasis: "80%",
    },

    [`& .${classes.helper}`]: {
      padding: "8px",
    },

    [`& .${classes.details}`]: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },

    [`& .${classes.selectFlag}`]: {
      "& > button": {
        background: theme.palette.primary.main,
        minWidth: "235px",
        "& > span": {
          color: "#303030",
        },
      },
      "& > ul": {
        background: "#424242",
        maxHeight: "150px",
        maxWidth: "180px",
        "& > div": {
          background: "#424242",
        },
      },
    },

    [`& .${classes.paintingContainer}`]: {
      height: "100%",
      display: "flex",
      justifyContent: "center",
      textAlign: "center",
    },

    [`& .${classes.formHeader}`]: {
      marginTop: "4rem",
    },

    [`& .${classes.editImageIcon}`]: {
      position: "absolute",
      zIndex: "99",
      right: "0",
      display: "flex",
    },

    [`& .${classes.flagStyle}`]: {
      height: "2rem",
      transform: "translate(15px, 20px)",
    },

    [`& .${classes.centerText}`]: {
      top: "2rem",
      textAlign: "center",
    },

    [`& .${classes.selectSizeContainer}`]: {
      margin: "1rem 0 1rem 0",
      [theme.breakpoints.between("xs", "md")]: {
        margin: "0 0 1rem 0",
      },
      width: "100%",
    },

    [`& .${classes.accordionContainer}`]: {
      minHeight: "425px",
    },

    [`& .${classes.saveSurveyButton}`]: {
      minWidth: "120px",
      "&:disabled": {
        backgroundColor: "transparent",
      },
    },

    [`& .${classes.selectImage}`]: {
      width: "60px",
      height: "60px",
    },

    [`& .${classes.menuItemContainer}`]: {
      display: "flex",
      flexDirection: "row",
      "& > img": {
        textAlign: "left",
        marginRight: "3rem",
      },
      "& > div": {
        textAlign: "left",
        marginRight: "3rem",
      },
    },

    [`& .${classes.selectText}`]: {
      transform: "translateY(20px)",
      marginRight: "3rem",
    },

    [`& .${classes.openMenuItem}`]: {
      "& > div > img": {
        textAlign: "left",
        marginRight: "3rem",
      },
      "& > div > div": {
        textAlign: "left",
        marginRight: "3rem",
      },
    },

    [`& .${classes.questionLogo}`]: {
      marginRight: "1rem",
      transform: "translateY(19px)",
    },

    [`& .${classes.centerButton}`]: {
      position: "relative",
      left: "50%",
      transform: "translateX(-50%)",
      marginBottom: "0.5rem",
    },

    [`& .${classes.chartLoaderContainer}`]: {
      textAlign: "center",
      marginTop: "1rem",
    },

    [`& .${classes.totalAnswers}`]: {
      textAlign: "center",
      marginTop: "1rem !important",
    },

    [`& .${classes.accordionRoot}`]: {
      backgroundColor: "#424242",
    },
    [`& .${classes.buttonSpaces}`]: {
      backgroundColor: "#424242",
      color: theme.palette.primary.main,
      marginRight: "15px",
      "&:hover": {
        backgroundColor: "rgba(66,66,66,0.7)",
        color: theme.palette.primary.main,
      },
    },
  };
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const Survey = (props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [openAccordion, setOpenAccordion] = useState(true);
  const [content, setContent] = useState(null);
  const [newIcon, setNewIcon] = useState(null);
  const [lastIndex, setLastIndex] = useState(null);
  const [open, setOpen] = useState(false);
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const { width } = useViewport();
  const [lockSaving, setLockSaving] = useState(false);
  const prevFrame = usePrevious(selectedFrame);
  const prevExparienceId = usePrevious(props.exparienceId);
  const [surveyExist, setSurveyExist] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [answersStats, setAnswersStats] = useState(null);

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  function toggleDialog() {
    setOpen((prevState) => !prevState);
  }

  function saveIcon() {
    let tmp = { ...content };
    tmp.answersLogo[lastIndex].file = document
      .getElementById("cropping-canvas")
      .toDataURL();
    setContent(tmp);
    toggleDialog();
  }

  const dispFlag = (code) => {
    return (
      <img
        loading="lazy"
        className={classes.flagStyle}
        src={`https://flagcdn.com/${code.toLowerCase()}.svg`}
        alt=""
      />
    );
  };

  function deleteLang(code) {
    let tmp = { ...content };
    var idx = -1;
    for (var i = 0; i < tmp.textData.length; i++) {
      if (tmp.textData[i].language === code) {
        idx = i;
        break;
      }
    }
    if (idx != -1) {
      tmp.textData.splice(idx, 1);
      if (tmp.textData.length === 0) {
        tmp.answersLogo = [];
        tmp.mainLogo = null;
      }
      setContent(tmp);
    }
  }

  function deleteAnswer(idx) {
    let tmp = { ...content };
    for (var i = 0; i < tmp.textData.length; i++) {
      tmp.textData[i].answers.splice(idx, 1);
    }
    tmp.answersLogo.splice(idx, 1);
    setContent(tmp);
  }

  function saveFileName(index, fileName) {
    let contentTmp = { ...content };
    contentTmp.answersLogo[index] = {
      file: null,
      fileName: Date.now() + "-" + fileName.replace(/\s+/g, ""),
    };
    setContent(contentTmp);
  }

  function handleImageReader(event, id) {
    let tmp = { ...urls };
    tmp[id] = URL.createObjectURL(event.target.files[0]);
    setUrls(tmp);
  }

  const handleOnChangeContent = (key, value, textDataIdx, answersIdx = 0) => {
    let tmp = { ...content };
    if (key === "answers") {
      tmp.textData[textDataIdx][key][answersIdx].text = value;
    } else {
      tmp.textData[textDataIdx][key] = value;
    }
    setContent(tmp);
  };

  const addAnswer = () => {
    let tmp = { ...content };
    if (tmp.textData.length > 0) {
      if (tmp.textData[0].answers.length <= 4) {
        tmp.textData.forEach((element) => {
          element.answers.push({ text: "" });
        });
        setContent(tmp);
      }
    }
  };

  const generateNewLanguage = (code, res = null) => {
    const tmp = { ...content };
    var alreadyExist = false;
    for (var i = 0; i < tmp.textData.length; i++) {
      if (code == tmp.textData[i].language) {
        alreadyExist = true;
        break;
      }
    }
    if (alreadyExist == false) {
      if (!res) {
        tmp.textData.push({
          language: code,
          question: tmp.textData.length > 0 ? tmp.textData[0].question : "",
          answers:
            tmp.textData.length > 0
              ? tmp.textData[0].answers.slice()
              : [{ text: "" }, { text: "" }],
        });
      } else {
        tmp.textData.push(res);
      }
      setContent(tmp);
    }
  };

  const formatLanguageForApi = (lang, reverse = false) => {
    let finalCode = lang;
    if (!reverse && lang === "GB") {
      finalCode = "EN";
    } else if (reverse && lang === "EN-GB") {
      finalCode = "GB";
    } else if (!reverse && lang === "CN") {
      finalCode = "ZH";
    } else if (reverse && lang === "ZH") {
      finalCode = "CN";
    } else if (!reverse && lang === "PT") {
      finalCode = "pt-PT";
    } else if (reverse && lang === "pt-PT") {
      finalCode = "PT";
    } else if (!reverse && lang === "US") {
      finalCode = "EN-US";
    } else if (reverse && lang === "EN-US") {
      finalCode = "US";
    } else if (!reverse && lang === "JP") {
      finalCode = "JA";
    } else if (reverse && lang === "JA") {
      finalCode = "JP";
    } else if (!reverse && lang === "GR") {
      finalCode = "El";
    } else if (reverse && lang === "EL") {
      finalCode = "GR";
    } else {
      finalCode = lang;
    }
    return finalCode;
  };

  const addLanguage = (code) => {
    if (content.textData.length === 0) {
      generateNewLanguage(code);
    } else {
      setIsTranslating(true);
      const { answersLogo, mainLogo, ...rest } = content;
      let finalCode = formatLanguageForApi(code);
      if (finalCode === "EN") finalCode = "EN-GB";
      const tmp = {
        configId: content.configId,
        textData: [],
      };
      content.textData.forEach((item) => {
        const { totalAnswers, answers, ...rest } = item;
        tmp.textData.push({
          ...rest,
          answers: answers.map((ans) => ({ ["_id"]: ans._id, text: ans.text })),
          language: formatLanguageForApi(item.language),
        });
      });
      myExpariencesService
        .translateSurvey(tmp, finalCode)
        .then((res) => {
          setIsTranslating(false);
          let tmp = { ...res };
          tmp.language = formatLanguageForApi(res.language, true);
          generateNewLanguage(code, tmp);
        })
        .catch((err) => {
          generateNewLanguage(code);
          setIsTranslating(false);
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("toasts.translateSurveyError"), {
              variant: "error",
            });
        });
    }
  };

  const toggleDialogAndReset = () => {
    const tmp = { ...content };
    tmp.answersLogo[lastIndex] = { file: "", fileName: "" };
    setContent(tmp);
    toggleDialog();
  };

  useEffect(() => {
    if (
      prevFrame &&
      prevExparienceId &&
      prevExparienceId === props.exparienceId
    ) {
      const frameId = props.frames.findIndex(
        (frame) => frame.id === prevFrame.id
      );
      if (frameId !== -1) setSelectedFrame(props.frames[frameId]);
    } else if (props.frames.length > 0) {
      setSelectedFrame(props.frames[0]);
    }
  }, [props.frames]);

  useEffect(() => {
    if (selectedFrame) {
      setLoading(true);
      myExpariencesService
        .getSurvey(selectedFrame.id)
        .then((res) => {
          const tmp = { ...res };
          if (!tmp.answersLogo) tmp.answersLogo = [];
          tmp.configId = selectedFrame.id;
          if (tmp.textData.length > 0) {
            setSurveyExist(true);
          } else {
            setSurveyExist(false);
          }
          setContent(tmp);
          if (res.hasOwnProperty("_id")) {
            myExpariencesService
              .getSurveysAnswers(res._id)
              .then((statRes) => {
                const tmp = res.textData;
                tmp.forEach((bloc) => {
                  bloc.totalAnswers = statRes["total"];
                  bloc.answers.forEach((elem) => {
                    elem.answer = statRes[elem._id];
                  });
                });
                setAnswersStats(tmp);
              })
              .catch((err) => {
                if (err === 403)
                  enqueueSnackbar(t("toasts.unauthorizedError"), {
                    variant: "error",
                  });
                else
                  enqueueSnackbar(t("toasts.fetchSurveyAnswersError"), {
                    variant: "error",
                  });
              });
            setLoading(false);
          } else {
            setLoading(false);
          }
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("toasts.fetchSurveyError"), { variant: "error" });
        });
    }
  }, [selectedFrame]);

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const handleDeleteAndCreateNew = () => {
    setLockSaving(true);
    myExpariencesService
      .deleteSurvey(selectedFrame.surveyId)
      .then(() => {
        const frameId = props.frames.findIndex(
          (frame) => frame.id === selectedFrame.id
        );
        const tmp = { ...selectedFrame, surveyId: null };
        const tmpFrames = [...props.frames];
        tmpFrames[frameId] = tmp;
        props.setFrames(tmpFrames);
        setLockSaving(false);
        enqueueSnackbar(t("toasts.deleteSurveySuccess"), {
          variant: "success",
        });
        setContent({
          answersLogo: null,
          configId: selectedFrame.id,
          mainLogo: null,
          textData: [],
        });
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.deleteSurveyError"), { variant: "error" });
        setLockSaving(false);
      });
  };

  const handleSaveSurvey = () => {
    setLockSaving(true);
    if (content.textData.length === 0) {
      myExpariencesService
        .deleteSurvey(selectedFrame.surveyId)
        .then(() => {
          const frameId = props.frames.findIndex(
            (frame) => frame.id === selectedFrame.id
          );
          const tmp = { ...selectedFrame, surveyId: null };
          const tmpFrames = [...props.frames];
          tmpFrames[frameId] = tmp;
          props.setFrames(tmpFrames);
          setLockSaving(false);
          enqueueSnackbar(t("toasts.deleteSurveySuccess"), {
            variant: "success",
          });
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("toasts.deleteSurveyError"), {
              variant: "error",
            });
          setLockSaving(false);
        });
    } else {
      const files = [];
      let tmpContent = {};
      tmpContent = { ...content };
      if (content.mainLogo && content.mainLogo.hasOwnProperty("file")) {
        files.push({ ...content.mainLogo, mainLogo: true });
        tmpContent.mainLogo = content.mainLogo.fileName;
      }
      const logos = [];
      content.answersLogo.forEach((elem) => {
        if (elem.hasOwnProperty("file")) {
          files.push(elem);
          logos.push(elem.fileName);
        } else {
          logos.push(elem);
        }
      });
      tmpContent.answersLogo = logos;
      tmpContent.configId = selectedFrame.id;
      myExpariencesService
        .saveExparienceSurvey(tmpContent, files)
        .then((res) => {
          const frameId = props.frames.findIndex(
            (frame) => frame.id === selectedFrame.id
          );
          const tmp = { ...selectedFrame, surveyId: res._id };
          const tmpFrames = [...props.frames];
          tmpFrames[frameId] = tmp;
          props.setFrames(tmpFrames);
          setLockSaving(false);
          enqueueSnackbar(t("toasts.saveSurveySuccess"), {
            variant: "success",
          });
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("toasts.saveSurveyError"), { variant: "error" });
          setLockSaving(false);
        });
    }
  };

  const isSaveDisabled = () => {
    let isDisabled = false;
    if (
      content.answersLogo?.length > 0 &&
      content.textData[0]?.answers?.length !== content.answersLogo?.length
    )
      isDisabled = true;
    content.answersLogo.forEach((logo) => {
      if (logo.hasOwnProperty("fileName") && logo.fileName.length === 0)
        isDisabled = true;
    });
    content.textData.forEach((elem) => {
      if (elem.question.length === 0) {
        isDisabled = true;
      }
      elem.answers.forEach((answer) => {
        if (answer.text?.length === 0) {
          isDisabled = true;
        }
      });
    });
    return isDisabled;
  };

  return (
    <SurveyContainer>
      <div className={classes.root}>
        <div>
          {props.frames.length <= 0 ? (
            <div
              style={{
                maxWidth: width > 1024 ? "80%" : "inherit",
                margin: "0 auto",
                marginBottom: width < 1024 ? "5rem" : "1rem",
              }}
            >
              <Typography className={classes.centerText}>
                {t("myExpariences.noFramesToDisplay")}
              </Typography>
            </div>
          ) : !selectedFrame ? (
            <div className={classes.loaderContainer}>
              <CircularProgress
                size={50}
                color="primary"
                className={classes.loadingIcon}
              />
              <Typography variant="body1">{t("loaders.saving")}</Typography>
            </div>
          ) : (
            <FormControl
              variant="outlined"
              className={classes.selectSizeContainer}
            >
              <InputLabel id="selectedFrame-survey-simple-select-outlined-label">
                {t("myExpariences.selectedFrameSurvey")}
              </InputLabel>
              <Select
                labelId="selectedFrame-survey-simple-select-outlined-label"
                id="selectedFrame-survey-simple-select-outlined"
                value={selectedFrame}
                onChange={(e) => setSelectedFrame(e.target.value)}
                label={t("myExpariences.selectedFrameSurvey")}
              >
                {props.frames.map((frame, index) => {
                  if (frame.hasOwnProperty("imageUrl"))
                    return (
                      <MenuItem
                        value={frame}
                        key={frame.id}
                        classes={{
                          root: classes.openMenuItem,
                        }}
                      >
                        <SurveyContainer>
                          <div className={classes.menuItemContainer}>
                            {frame.imageUrl ? (
                              <img
                                src={frame.imageUrl}
                                alt={`frame-${index + 1}`}
                                className={classes.selectImage}
                              />
                            ) : (
                              <div className={classes.selectImage} />
                            )}
                            {frame.hasOwnProperty("surveyId") &&
                              frame.surveyId && (
                                <QuestionAnswerIcon
                                  className={classes.questionLogo}
                                />
                              )}
                            <span className={classes.selectText}>
                              {frame.title
                                ? frame.title
                                : frame.imageUrl
                                    .split("/")
                                    [
                                      frame.imageUrl.split("/").length - 1
                                    ].split(".")[0]}
                            </span>
                          </div>
                        </SurveyContainer>
                      </MenuItem>
                    );
                  else return null;
                })}
              </Select>
            </FormControl>
          )}
        </div>
        <ResponsiveDialogManager
          title={t("myExpariences.watermarkExternalCropModalTitle")}
          open={open}
          onClose={toggleDialog}
          renderer={
            <ImageCropper
              imgUrl={newIcon}
              aspect={1 / 1}
              urlSetter={setNewIcon}
            />
          }
          toolbarActions={
            <SurveyContainer>
              <Button className={classes.buttonSpaces} onClick={saveIcon}>
                {t("account.validate")}
              </Button>
              <Button
                className={classes.buttonSpaces}
                onClick={toggleDialogAndReset}
              >
                {t("account.cancel")}
              </Button>
            </SurveyContainer>
          }
        />
        {!loading && (
          <div className={classes.accordionContainer}>
            {surveyExist && (
              <>
                <ConfirmationDialog
                  open={openConfirmation}
                  setOpen={setOpenConfirmation}
                  onAgree={() => handleDeleteAndCreateNew()}
                  mode="deleteSurvey"
                />
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.centerButton}
                  onClick={() => setOpenConfirmation(true)}
                >
                  {t("myExpariences.form.newSurvey")}
                </Button>
              </>
            )}
            <Accordion
              open={openAccordion}
              defaultExpanded
              onChange={(e, expanded) => setOpenAccordion(expanded)}
              className={classes.accordionRoot}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="primary" />}
                aria-controls="survey-content"
                id="survey-header"
              >
                <div className={classes.iconColumn}>
                  <Typography className={classes.heading}>
                    {t("myExpariences.surveyTitle")}
                  </Typography>
                </div>
                {/* <div>
                {`${content.textData.length} ${t('myExpariences.surveyTraductions')}`}
              </div> */}
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <Tabs
                  value={tabValue}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={handleChange}
                  variant="fullWidth"
                >
                  <Tab label={t("myExpariences.surveyContent")} />
                  <Tab label={t("myExpariences.surveyAnswersStats")} />
                </Tabs>
                <TabPanel value={tabValue} index={0}>
                  <div className={classes.imageContainer}>
                    <div className={classes.topContainer}>
                      <Typography variant="h6">
                        {t("myExpariences.surveyMainLogo")}
                      </Typography>
                      {((content.mainLogo &&
                        !content.mainLogo.hasOwnProperty("file")) ||
                        (content.mainLogo?.hasOwnProperty("file") &&
                          content.mainLogo.file)) &&
                        openAccordion && (
                          <div className={classes.editImageIcon}>
                            <input
                              type="file"
                              accept="image/*"
                              name="survey-main-logo"
                              id="modify-survey-main-logo"
                              style={{ display: "none" }}
                              onChange={(e) => {
                                handleImageReader(e, "surveyMainLogo");
                                const tmp = { ...content };
                                tmp.mainLogo = {
                                  file: e.target.files[0],
                                  fileName: e.target.files[0].name,
                                };
                                setContent(tmp);
                              }}
                            />
                            <label htmlFor="modify-survey-main-logo">
                              <IconButton
                                color="primary"
                                aria-label="upload picture"
                                component="span"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                size="large"
                              >
                                <CreateIcon />
                              </IconButton>
                            </label>
                            <div>
                              <input
                                name="delete-survey-main-logo"
                                id="delete-survey-main-logo"
                                style={{ display: "none" }}
                              />
                              <label htmlFor="delete-survey-main-logo">
                                <IconButton
                                  color="primary"
                                  aria-label="delete survey main logo"
                                  component="span"
                                  onClick={(e) => {
                                    const tmpUrls = { ...urls };
                                    URL.revokeObjectURL(tmpUrls.surveyMainLogo);
                                    delete tmpUrls.surveyMainLogo;
                                    setUrls(tmpUrls);
                                    const tmp = { ...content };
                                    tmp.mainLogo = null;
                                    setContent(tmp);
                                  }}
                                  size="large"
                                >
                                  <ClearIcon />
                                </IconButton>
                              </label>
                            </div>
                          </div>
                        )}
                    </div>
                    <div className={classes.paintingContainer}>
                      <img
                        id="surveyMainLogo"
                        className={classes.formHeader}
                        style={{ width: content.mainLogo ? "60%" : "0" }}
                        alt={
                          (content.mainLogo &&
                            !content.mainLogo.hasOwnProperty("file")) ||
                          (content.mainLogo?.hasOwnProperty("file") &&
                            content.mainLogo.file)
                            ? "Survey main logo"
                            : null
                        }
                        src={
                          content.mainLogo?.hasOwnProperty("file")
                            ? urls.surveyMainLogo
                            : content.mainLogo
                            ? content.mainLogo
                            : undefined
                        }
                      />
                      {!content.mainLogo && openAccordion && (
                        <div style={{ textAlign: "center", marginTop: "1rem" }}>
                          <input
                            type="file"
                            accept="image/*"
                            name="surveyMainLogo"
                            id="icon-button-survey-main-logo-add"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              handleImageReader(e, "surveyMainLogo");
                              const tmp = { ...content };
                              tmp.mainLogo = {
                                file: e.target.files[0],
                                fileName: e.target.files[0].name,
                              };
                              setContent(tmp);
                            }}
                          />
                          <label htmlFor="icon-button-survey-main-logo-add">
                            <Button
                              variant="contained"
                              color="primary"
                              component="span"
                              size="small"
                              className={classes.addCustomWatermarkButton}
                            >
                              {t("myExpariences.watermarkExternalAddButton")}
                            </Button>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                  <Divider
                    style={{ marginTop: "1rem", marginBottom: "1rem" }}
                  />
                  {!isTranslating ? (
                    <div style={{ display: "flex" }}>
                      <div style={{ marginLeft: "1rem", width: "11rem" }}>
                        <ReactFlagsSelect
                          className={classes.selectFlag}
                          placeholder={t("myExpariences.form.addLanguage")}
                          // optionsSize={20}
                          countries={authorizedCountries}
                          showSelectedLabel={false}
                          // showOptionLabel={false}
                          selected="currentLanguage"
                          onSelect={(code) => addLanguage(code)}
                        />
                      </div>
                      {content.textData.length > 0 && (
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<AddIcon />}
                          style={{ marginLeft: "1rem" }}
                          onClick={() => addAnswer()}
                        >
                          {t("myExpariences.form.addAnswers")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <CircularProgress size={28} />
                  )}
                  {content.textData.map((element, idx) => {
                    return (
                      <div key={`form-container-${idx + 1}`}>
                        <Divider
                          style={{
                            marginTop: "1rem",
                            marginBottom: "1rem",
                            height: "0.2rem",
                          }}
                        />
                        <div style={{ position: "relative", height: "15px" }}>
                          <IconButton
                            color="primary"
                            style={{
                              marginLeft: "-0.5rem",
                              position: "absolute",
                              right: 0,
                              top: "-15px",
                            }}
                            aria-label="delete answer"
                            component="span"
                            onClick={(e) => {
                              deleteLang(element.language);
                            }}
                            size="large"
                          >
                            <ClearIcon />
                          </IconButton>
                        </div>
                        <div style={{ display: "flex" }}>
                          <div
                            className={clsx(classes.linkColumn, classes.helper)}
                          >
                            <TextField
                              name={`Question`}
                              onChange={(e) =>
                                handleOnChangeContent(
                                  "question",
                                  e.target.value,
                                  idx
                                )
                              }
                              label={t("myExpariences.form.question")}
                              value={element.question}
                              fullWidth
                              variant={"outlined"}
                            />
                          </div>
                          {dispFlag(element.language)}
                        </div>
                        {element.answers.map((ans, i) => {
                          return (
                            <div
                              className={clsx(
                                classes.linkColumn,
                                classes.helper
                              )}
                              key={`answer-${idx + 1}-${i + 1}`}
                              style={{ marginTop: "1rem" }}
                            >
                              <div>
                                <div>
                                  <div>
                                    {((content.answersLogo[i] !== undefined &&
                                      typeof content.answersLogo[i] ===
                                        "string") ||
                                      (typeof content.answersLogo[i] !==
                                        "string" &&
                                        content.answersLogo[i]?.file)) && (
                                      <div style={{ textAlign: "right" }}>
                                        <input
                                          type="file"
                                          accept="image/*"
                                          name={`modify-custom-question-icon-${
                                            i + 1
                                          }`}
                                          id={`modify-custom-question-icon-${
                                            i + 1
                                          }`}
                                          style={{ display: "none" }}
                                          onChange={(e) => {
                                            const reader = new FileReader();
                                            reader.onloadend = (value) => {
                                              setNewIcon(value.target.result);
                                            };
                                            toggleDialog();
                                            saveFileName(
                                              i,
                                              e.target.files[0].name
                                            );
                                            reader.readAsDataURL(
                                              e.target.files[0]
                                            );
                                            e.target.value = "";
                                          }}
                                        />
                                        <label
                                          htmlFor={`modify-custom-question-icon-${
                                            i + 1
                                          }`}
                                        >
                                          <IconButton
                                            color="primary"
                                            aria-label="give question icon"
                                            component="span"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setLastIndex(i);
                                            }}
                                            size="large"
                                          >
                                            <CreateIcon />
                                          </IconButton>
                                        </label>
                                      </div>
                                    )}
                                    {(content.answersLogo[i] === undefined ||
                                      (content.answersLogo[i] &&
                                        typeof content.answersLogo[i] !==
                                          "string" &&
                                        !content.answersLogo[i].file)) && (
                                      <div style={{ textAlign: "center" }}>
                                        <input
                                          type="file"
                                          accept="image/*"
                                          name={`button-add-answer-icon-${
                                            i + 1
                                          }`}
                                          id={`icon-button-add-answer-icon-${
                                            i + 1
                                          }`}
                                          style={{ display: "none" }}
                                          onChange={(e) => {
                                            const reader = new FileReader();
                                            reader.onloadend = (value) => {
                                              setNewIcon(value.target.result);
                                            };
                                            toggleDialog();
                                            saveFileName(
                                              i,
                                              e.target.files[0].name
                                            );
                                            reader.readAsDataURL(
                                              e.target.files[0]
                                            );
                                            e.target.value = "";
                                          }}
                                        />
                                        <label
                                          htmlFor={`icon-button-add-answer-icon-${
                                            i + 1
                                          }`}
                                        >
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            component="span"
                                            style={{
                                              width: "2rem",
                                              minWidth: "0rem",
                                            }}
                                            size="small"
                                            className={
                                              classes.addCustomWatermarkButton
                                            }
                                            onClick={() => setLastIndex(i)}
                                          >
                                            {/* {t('myExpariences.watermarkExternalAddButton')} */}
                                            <PhotoCamera />
                                          </Button>
                                        </label>
                                      </div>
                                    )}
                                  </div>
                                  <img
                                    style={{
                                      display: "block",
                                      margin: "0px auto",
                                      marginBottom: "2rem",
                                      maxHeight: "5rem",
                                    }}
                                    id="customWatermarkIcon"
                                    className={classes.iconImage}
                                    alt={
                                      content.answersLogo[i] !== undefined &&
                                      (typeof content.answersLogo[i] ===
                                        "string" ||
                                        content.answersLogo[i].file)
                                        ? `answer logo ${i + 1}`
                                        : null
                                    }
                                    src={
                                      content.answersLogo[i] !== undefined
                                        ? typeof content.answersLogo[i] ===
                                          "string"
                                          ? content.answersLogo[i]
                                          : content.answersLogo[i].file
                                          ? content.answersLogo[i].file
                                          : undefined
                                        : undefined
                                    }
                                  />
                                </div>
                                <div style={{ display: "flex" }}>
                                  <TextField
                                    name={`Réponse`}
                                    onChange={(e) =>
                                      handleOnChangeContent(
                                        "answers",
                                        e.target.value,
                                        idx,
                                        i
                                      )
                                    }
                                    label={t("myExpariences.form.answer")}
                                    fullWidth
                                    value={ans.text}
                                    variant={"outlined"}
                                  />
                                  <IconButton
                                    color="primary"
                                    style={{ marginRight: "-0.3rem" }}
                                    aria-label="delete answer"
                                    component="span"
                                    onClick={(e) => {
                                      deleteAnswer(i);
                                    }}
                                    size="large"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  {answersStats ? (
                    <div>
                      <BarChart isSurvey answersStats={answersStats} />
                      <Typography
                        variant="body1"
                        className={classes.totalAnswers}
                      >
                        {t("myExpariences.form.totalSurveyAnswers")}
                        {answersStats[0]?.totalAnswers}
                      </Typography>
                    </div>
                  ) : (
                    <div className={classes.chartLoaderContainer}>
                      <CircularProgress
                        size={50}
                        color="primary"
                        className={classes.loadingIcon}
                      />
                      <Typography variant="body1">
                        {t("loaders.fetching")}
                      </Typography>
                    </div>
                  )}
                </TabPanel>
              </AccordionDetails>
              <Divider />
              <AccordionActions style={{ textAlign: "right" }}>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={() => handleSaveSurvey()}
                  disabled={isSaveDisabled() || lockSaving || isTranslating}
                  className={classes.saveSurveyButton}
                >
                  {lockSaving ? (
                    <CircularProgress size={28} />
                  ) : (
                    t("myExpariences.surveySaveButton")
                  )}
                </Button>
              </AccordionActions>
            </Accordion>
          </div>
        )}
      </div>
    </SurveyContainer>
  );
};

export default Survey;
